import { type WsEsterno } from '@framework/api-client/api/users/schemas/wsEsterno.ts';
import { type WsEsternoParametroServizio } from '@framework/api-client/api/users/schemas/wsEsternoParametroServizio.ts';
import { WsEsternoParametroServizioTipo } from '@framework/api-client/api/users/schemas/wsEsternoParametroServizioTipo.ts';
import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import {
  AsyncServiceMethod,
  type IAsyncService,
  type IAsyncServiceParameter,
} from '@framework/engine/schema/expressions/repository/async/context/IAsyncService.ts';
import { mapAsyncServiceField } from '@framework/engine/schema/expressions/repository/async/mapAsyncServiceField.ts';
import { isAbsoluteURL } from '@framework/engine/schema/expressions/repository/async/url/parseAsyncServiceUrl.ts';

/**
 * Converte un servizio esterno (`WsEsterno`, modello di trasporto delle API)
 * in un servizio asincrono utilizzabile dall'engine (`IAsyncService`).
 *
 * Vive in `common-web` (e non nell'engine) perché dipende dai DTO di
 * `@framework/api-client`: l'engine non può dipendere da api-client. È riusata
 * da FE (`useAsyncServices`), BE (`ExternalServiceCache`) e preflight
 * (`AsyncServiceVerifier`) per garantire una conversione identica.
 */
export function transformWsEsternoInAsyncService(
  webservice: WsEsterno,
): IAsyncService {
  return {
    id: webservice.id ?? '<unknown>',
    name: webservice.denominazione,
    url: webservice.url,
    method: toAsyncServiceMethod(webservice.metodo),
    inputs:
      webservice.input?.map(input =>
        transformWsEsternoParametroInAsyncServiceParameter(input),
      ) ?? [],
    outputs:
      webservice.output?.parametri?.map(output =>
        transformWsEsternoParametroInAsyncServiceParameter(output),
      ) ?? [],
    type: {
      id: webservice.tipoWsEsterno.id,
      name: webservice.tipoWsEsterno.nome,
    },
    outputRoot: webservice.output?.root,
    // I servizi con url assoluto puntano a servizi esterni di terze parti
    isExternal: isAbsoluteURL(webservice.url),
  };
}

/**
 * Normalizza il metodo HTTP del servizio esterno (campo libero e opzionale sul
 * DTO) in un `AsyncServiceMethod` valido. Per metodo assente o non riconosciuto
 * si usa GET come default, evitando che a runtime `method` resti non valorizzato
 * e che la chiamata Axios assuma una semantica errata.
 */
function toAsyncServiceMethod(
  metodo: string | null | undefined,
): AsyncServiceMethod {
  switch (metodo?.toUpperCase()) {
    case AsyncServiceMethod.POST:
      return AsyncServiceMethod.POST;
    case AsyncServiceMethod.PUT:
      return AsyncServiceMethod.PUT;
    default:
      return AsyncServiceMethod.GET;
  }
}

function transformWsEsternoParametroInAsyncServiceParameter(
  parameter: WsEsternoParametroServizio,
): IAsyncServiceParameter {
  return {
    // Nome "flatten" del campo in root
    name: mapAsyncServiceField(parameter.nome),
    // Path originale del campo
    sourcePath: parameter.nome,
    type: parameter.array
      ? {
          type: Datatype.Array,
          itemsType: tipoToDatatype(parameter.tipo),
        }
      : { type: tipoToDatatype(parameter.tipo) },
  };
}

function tipoToDatatype(tipo: WsEsternoParametroServizioTipo) {
  switch (tipo) {
    case WsEsternoParametroServizioTipo.BOOLEAN:
      return Datatype.Boolean;
    case WsEsternoParametroServizioTipo.DATE:
      return Datatype.Date;
    case WsEsternoParametroServizioTipo.NUMBER:
      return Datatype.Number;
    case WsEsternoParametroServizioTipo.STRING:
      return Datatype.String;
    default:
      return Datatype.String;
  }
}
